import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { money } from "@/lib/format";
import { geocode } from "@/lib/location";
import { Field, NativeSelect } from "@/components/site/bits";
import { Logo } from "@/components/site/Navbar";
import MapView from "@/components/site/MapView";

export function BranchFields({ b, setB, prefix = "branch" }) {
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });
  const locate = async () => {
    const r = await geocode(`${b.address}, ${b.city}`).catch(() => []);
    if (r[0]) setB({ ...b, lat: r[0].lat, lng: r[0].lng });
    else toast.error("Address not found — click on the map to drop the pin.");
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Branch name"><Input data-testid={`${prefix}-name-input`} required value={b.name} onChange={set("name")} /></Field>
      <Field label="Branch phone"><Input data-testid={`${prefix}-phone-input`} value={b.phone} onChange={set("phone")} /></Field>
      <div className="sm:col-span-2"><Field label="Branch address"><Input data-testid={`${prefix}-address-input`} required value={b.address} onChange={set("address")} /></Field></div>
      <Field label="City"><Input data-testid={`${prefix}-city-input`} required value={b.city} onChange={set("city")} /></Field>
      <Field label="Opening hours"><Input data-testid={`${prefix}-hours-input`} value={b.opening_hours} onChange={set("opening_hours")} placeholder="9 AM – 10 PM" /></Field>
      <Field label="Latitude"><Input data-testid={`${prefix}-lat-input`} type="number" step="any" required value={b.lat} onChange={(e) => setB({ ...b, lat: +e.target.value })} /></Field>
      <Field label="Longitude"><Input data-testid={`${prefix}-lng-input`} type="number" step="any" required value={b.lng} onChange={(e) => setB({ ...b, lng: +e.target.value })} /></Field>
      <div className="sm:col-span-2 space-y-2">
        <button type="button" data-testid={`${prefix}-locate-button`} onClick={locate} className="text-sm font-semibold text-red-500 flex items-center gap-1"><Crosshair className="w-4 h-4" /> Find address on map</button>
        <MapView lat={+b.lat || 13.0827} lng={+b.lng || 80.2707} height={240} zoom={14} onPick={(p) => setB({ ...b, ...p })} />
        <p className="text-xs text-slate-500">Click the map to set the exact store pin.</p>
      </div>
    </div>
  );
}

export const emptyBranch = { name: "", address: "", city: "", state: "", country: "India", postal_code: "", lat: 13.0827, lng: 80.2707, phone: "", opening_hours: "", timezone: "Asia/Kolkata" };

export default function SellerRegister() {
  const { applySession } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ business_name: "", contact_name: "", phone: "", email: "", password: "", category_id: "", description: "", gstin: "", address: "", city: "", state: "", country: "India", postal_code: "", preferred_package_id: "" });
  const [b, setB] = useState(emptyBranch);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/packages").then((r) => setPkgs(r.data));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = (await api.post("/seller/register", { ...f, preferred_package_id: f.preferred_package_id || null, branch: { ...b, city: b.city || f.city, state: f.state, postal_code: f.postal_code } })).data;
      applySession(data);
      toast.success("Registration submitted — pending approval");
      nav("/seller");
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200"><div className="max-w-4xl mx-auto px-4 h-16 flex items-center"><Logo /></div></header>
      <form onSubmit={submit} className="max-w-4xl mx-auto px-4 py-10 space-y-8">
        <div><h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Register your store</h1><p className="text-slate-500 mt-1">Accounts start as Pending Approval. Our team reviews and activates your package — no online payment needed.</p></div>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
          <h2 className="sm:col-span-2 font-heading font-bold text-lg">Business details</h2>
          <Field label="Business name"><Input data-testid="seller-business-name-input" required value={f.business_name} onChange={set("business_name")} /></Field>
          <Field label="Business category"><NativeSelect data-testid="seller-category-select" required value={f.category_id} onChange={set("category_id")}><option value="">Select…</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect></Field>
          <Field label="Contact person"><Input data-testid="seller-contact-input" required value={f.contact_name} onChange={set("contact_name")} /></Field>
          <Field label="Mobile number"><Input data-testid="seller-phone-input" required value={f.phone} onChange={set("phone")} placeholder="+919876543210" /></Field>
          <Field label="Email"><Input data-testid="seller-email-input" type="email" required value={f.email} onChange={set("email")} /></Field>
          <Field label="Password" hint="At least 8 characters"><Input data-testid="seller-password-input" type="password" minLength={8} required value={f.password} onChange={set("password")} /></Field>
          <Field label="GSTIN / tax registration (optional)"><Input data-testid="seller-gstin-input" value={f.gstin} onChange={set("gstin")} /></Field>
          <Field label="Preferred package"><NativeSelect data-testid="seller-package-select" value={f.preferred_package_id} onChange={set("preferred_package_id")}><option value="">Decide later</option>{pkgs.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.slots} deals · {money(p.activation_price)}</option>)}</NativeSelect></Field>
          <div className="sm:col-span-2"><Field label="Business description"><Textarea data-testid="seller-description-input" value={f.description} onChange={set("description")} /></Field></div>
          <div className="sm:col-span-2"><Field label="Business address"><Input data-testid="seller-address-input" required value={f.address} onChange={set("address")} /></Field></div>
          <Field label="City"><Input data-testid="seller-city-input" required value={f.city} onChange={set("city")} /></Field>
          <Field label="State"><Input data-testid="seller-state-input" value={f.state} onChange={set("state")} /></Field>
          <Field label="Country"><Input data-testid="seller-country-input" value={f.country} onChange={set("country")} /></Field>
          <Field label="Postal code"><Input data-testid="seller-postal-input" value={f.postal_code} onChange={set("postal_code")} /></Field>
          <p className="sm:col-span-2 text-xs text-slate-500">You can upload your logo and cover image from your seller profile after registering.</p>
        </section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading font-bold text-lg">First branch</h2>
          <BranchFields b={b} setB={setB} />
        </section>
        <button data-testid="seller-register-submit" disabled={busy} className="w-full sm:w-auto px-10 h-12 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Submit registration</button>
      </form>
    </div>
  );
}
